/**
 * Minimal, dependency-free markdown renderer.
 *
 * Deliberately small: headings, lists, fenced and inline code, blockquotes,
 * rules, bold/italic/strikethrough, links, and paragraphs. Everything is HTML
 * escaped *before* any markup is generated, and links are restricted to
 * http/https/mailto/relative, so ticket bodies and comments cannot inject markup.
 */

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }

/** HTML-escape a raw string. */
export function escapeHtml(text) {
  return String(text).replace(/[\uE000\uE001]/g, '').replace(/[&<>"']/g, (c) => ESCAPES[c])
}

function safeUrl(url) {
  const trimmed = url.trim()
  return /^(https?:\/\/|mailto:|\/)/i.test(trimmed) ? trimmed : '#'
}

const CODE_OPEN = '\uE000'
const CODE_CLOSE = '\uE001'

/** Apply inline-level markup to already-escaped text. */
function inline(escaped) {
  const codes = []
  let out = escaped.replace(/`([^`]+)`/g, (_, code) => {
    codes.push(code)
    return CODE_OPEN + (codes.length - 1) + CODE_CLOSE
  })
  out = out
    .replace(
      /\[([^\]]+)\]\(([^)\s]+)\)/g,
      (_, label, href) =>
        `<a href="${escapeHtml(safeUrl(href))}" target="_blank" rel="noreferrer noopener">${label}</a>`,
    )
    .replace(/(^|[^*])\*\*([^*]+)\*\*/g, '$1<strong>$2</strong>')
    .replace(/(^|[^*\w])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/~~([^~]+)~~/g, '<del>$1</del>')
  const placeholder = new RegExp(CODE_OPEN + '(\\d+)' + CODE_CLOSE, 'g')
  return out.replace(placeholder, (_, i) => `<code>${codes[Number(i)]}</code>`)
}

/**
 * Render markdown source to an HTML string safe for `dangerouslySetInnerHTML`.
 *
 * @param {string} src Markdown text.
 * @returns {string} HTML.
 */
export function renderMarkdown(src) {
  if (!src) return ''
  const lines = escapeHtml(src).replace(/\r\n/g, '\n').split('\n')
  const html = []
  let listType = null
  let paragraph = []

  const flushParagraph = () => {
    if (paragraph.length) {
      html.push(`<p>${inline(paragraph.join(' '))}</p>`)
      paragraph = []
    }
  }
  const closeList = () => {
    if (listType) {
      html.push(`</${listType}>`)
      listType = null
    }
  }

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]

    const fence = line.match(/^\s*```(\S*)\s*$/)
    if (fence) {
      flushParagraph()
      closeList()
      const body = []
      i += 1
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) {
        body.push(lines[i])
        i += 1
      }
      const lang = fence[1] ? ` class="lang-${fence[1].replace(/[^\w-]/g, '')}"` : ''
      html.push(`<pre><code${lang}>${body.join('\n')}</code></pre>`)
      continue
    }

    if (!line.trim()) {
      flushParagraph()
      closeList()
      continue
    }

    if (/^\s*(---|\*\*\*|___)\s*$/.test(line)) {
      flushParagraph()
      closeList()
      html.push('<hr />')
      continue
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/)
    if (heading) {
      flushParagraph()
      closeList()
      const level = Math.min(heading[1].length, 6)
      html.push(`<h${level}>${inline(heading[2])}</h${level}>`)
      continue
    }

    const quote = line.match(/^\s*&gt;\s?(.*)$/)
    if (quote) {
      flushParagraph()
      closeList()
      html.push(`<blockquote>${inline(quote[1])}</blockquote>`)
      continue
    }

    const bullet = line.match(/^\s*[-*+]\s+(.*)$/)
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/)
    if (bullet || numbered) {
      flushParagraph()
      const wanted = bullet ? 'ul' : 'ol'
      if (listType !== wanted) {
        closeList()
        html.push(`<${wanted}>`)
        listType = wanted
      }
      const item = (bullet ? bullet[1] : numbered[1])
        .replace(/^\[ \]\s*/, '☐ ')
        .replace(/^\[[xX]\]\s*/, '☑ ')
      html.push(`<li>${inline(item)}</li>`)
      continue
    }

    closeList()
    paragraph.push(line.trim())
  }

  flushParagraph()
  closeList()
  return html.join('\n')
}
